import { forwardRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { ChevronRight } from 'lucide-react';

interface AccordionFieldShellProps {
  /** Stable id for the section header — wires aria-labelledby on the surrounding region. */
  fieldName: string;
  /** Short label rendered as the primary heading (e.g. field name, possibly localised). */
  title: ReactNode;
  /** Optional small descriptor under the title (e.g. "23 filas · grupo"). */
  subtitle?: ReactNode;
  /** Optional confidence/score visual rendered to the left of the title. */
  leading?: ReactNode;
  /** Optional pill/badge rendered next to the title (e.g. "Grupo", "Tabla", "Editado"). */
  badge?: ReactNode;
  /** Optional inline summary or warning line shown beneath the title row. */
  meta?: ReactNode;
  /** Default open state on first render. The user can still toggle it manually afterwards. */
  defaultOpen?: boolean;
  /** True when this section is the currently-highlighted target (sync with PDF). */
  highlighted?: boolean;
  /** Body content, hidden behind the collapse animation when closed. */
  children: ReactNode;
}

/**
 * Generic shell for an accordion row in the reviewer panel. Used by both `GroupFieldRow`
 * (nested object fields) and `TableFieldRow` (table-typed fields) so the affordance is
 * identical regardless of the underlying field type.
 *
 * The collapse animation uses `grid-template-rows: 0fr → 1fr`. That technique animates
 * height without measuring the content, avoiding the layout thrash of a JS-driven
 * `max-height` transition while staying within plain Tailwind utilities.
 */
export const AccordionFieldShell = forwardRef<HTMLDivElement, AccordionFieldShellProps>(
  function AccordionFieldShell(
    {
      fieldName,
      title,
      subtitle,
      leading,
      badge,
      meta,
      defaultOpen = false,
      highlighted = false,
      children,
    },
    ref,
  ): JSX.Element {
    const [open, setOpen] = useState<boolean>(defaultOpen);
    const labelId = `accordion-${fieldName}-label`;
    const panelId = `accordion-${fieldName}-panel`;

    return (
      <div
        ref={ref}
        role="group"
        aria-labelledby={labelId}
        className={clsx(
          'group relative overflow-hidden rounded-2xl border bg-white shadow-sm transition-all duration-200 ease-out',
          'hover:-translate-y-0.5 hover:shadow-md',
          highlighted ? 'border-primary/60 shadow-lg ring-2 ring-primary/30' : 'border-slate-200/70',
        )}
      >
        <button
          type="button"
          className="flex w-full items-start gap-4 p-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={panelId}
        >
          {leading !== undefined ? <div className="flex-none pt-0.5">{leading}</div> : null}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <ChevronRight
                aria-hidden
                strokeWidth={2.25}
                className={clsx(
                  'h-4 w-4 flex-none text-slate-500 transition-transform duration-200 ease-out',
                  open && 'rotate-90',
                )}
              />
              <span
                id={labelId}
                className="truncate font-mono text-sm font-semibold tracking-tight text-slate-900"
              >
                {title}
              </span>
              {badge}
              {subtitle !== undefined ? (
                <span className="text-xs text-slate-500">{subtitle}</span>
              ) : null}
            </div>
            {meta !== undefined ? <div className="mt-1.5">{meta}</div> : null}
          </div>
        </button>

        {/* Animated collapse: grid-template-rows 0fr → 1fr lets the child grow/shrink
            without us having to measure its height. The child container needs min-h-0
            and overflow-hidden so the clipped area is invisible while closed. */}
        <div
          id={panelId}
          className={clsx(
            'grid transition-[grid-template-rows] duration-200 ease-out',
            open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
          )}
          aria-hidden={!open}
        >
          <div className="min-h-0 overflow-hidden">
            <div className="border-t border-slate-100 bg-slate-50/60 p-4">{children}</div>
          </div>
        </div>
      </div>
    );
  },
);
